import React, { useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import QRCode from "qrcode"; // Importer qrcode-biblioteket
import Cookies from 'js-cookie';
import { escapeHtml } from './utils/format';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ClubOrderDetails() {
    const { vippsOrderId, orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [qrCodes, setQrCodes] = useState({}); 
    const [customerExists, setCustomerExists] = useState(null); // Ny state for telefonnummer-sjekk
    const [customerData, setCustomerData] = useState(null); // Kundedata fra databasen
    const [statusUpdated, setStatusUpdated] = useState(false); // Viser melding ved oppdatering
    const [originalCustomerId, setOriginalCustomerId] = useState(null);
    const hasCheckedCustomer = useRef(false);
    const [showCustomerInfo, setShowCustomerInfo] = useState(false);
    const [showPhoneCheck, setShowPhoneCheck] = useState(false);

// Funksjon for å vise/skjule telefonnummer-sjekken
const togglePhoneCheck = () => {
    setShowPhoneCheck(!showPhoneCheck);
};


// Funksjon for å vise/skjule informasjonen
const toggleCustomerInfo = () => {
    setShowCustomerInfo(!showCustomerInfo);
};


    const checkPhoneNumberInDatabase = useCallback(async (phoneNumber, clubCustomerId, orderId, customerInfo) => {
        try {
            const response = await fetch(`${API_BASE_URL}/customers?phoneNumber=${phoneNumber}`);
            if (response.ok) {
                const customers = await response.json();
                const matchedCustomer = customers.find(customer => 
                    normalizePhoneNumber(customer.phoneNumber) === normalizePhoneNumber(phoneNumber)
                );
    
                if (matchedCustomer) {
                    setCustomerExists(true);
                    setCustomerData(matchedCustomer);
                    setOriginalCustomerId(matchedCustomer._id);
                    await updateClubCustomerAndOrderWithOriginalId(clubCustomerId, matchedCustomer._id, orderId);
                    return;
                }
            }
    
            // Hvis kunden ikke finnes, opprett en ny og OPPDATER customerExists
            if (!customerInfo) {
                console.error("Feil: customerInfo er undefined, kan ikke opprette kunde.");
                return;
            }
    
            const newCustomerId = await createNewCustomer(customerInfo);
            if (newCustomerId) {
                setOriginalCustomerId(newCustomerId);
                setCustomerExists(true);  // Viktig for å fjerne "Laster telefonnummer..."
                await updateClubCustomerAndOrderWithOriginalId(clubCustomerId, newCustomerId, orderId);
            } else {
                setCustomerExists(false);
            }
        } catch (error) {
            console.error("Feil ved sjekk/opprettelse av kunde:", error);
            setCustomerExists(false);
        }
    }, []);
    
    // Finn den siste storeId som er registrert i listen over produkter
const sisteStoreId = order?.items?.filter(item => item.storeId)?.slice(-1)[0]?.storeId || null;

// Ekstraher det siste sifferet fra sisteStoreId som butikkIdOrder
const butikkIdOrder = sisteStoreId && typeof sisteStoreId === 'string'
  ? sisteStoreId.trim().slice(-1)
  : sisteStoreId && typeof sisteStoreId === 'number'
  ? sisteStoreId.toString().slice(-1)
  : null;

  const getHentested = (butikkIdOrder) => {
    switch (butikkIdOrder) {
        case "1":
            return "Sport 1 Røyken";
        case "2":
            return "Sport 1 Slemmestad";
        default:
            return "Ukjent butikk";
    }
};

    useEffect(() => {
        const fetchOrderDetails = async () => {
          try {
            const id = orderId || vippsOrderId;
      
            const endpoint = orderId
              ? `${API_BASE_URL}/cluborders/${id}`
              : `${API_BASE_URL}/cluborders/by-vipps/${id}`;
      
            const response = await fetch(endpoint);
            if (!response.ok) throw new Error("Kunne ikke hente ordredata");
      
            const data = await response.json();
            setOrder(data);
      
      
            if (data.customerId?.phoneNumber && !hasCheckedCustomer.current) {
              hasCheckedCustomer.current = true;
              await checkPhoneNumberInDatabase(
                data.customerId.phoneNumber,
                data.customerId._id,
                data._id,
                data.customerId
              );
            }
          } catch (err) {
            setError(err.message);
          } finally {
            setLoading(false);
          }
        };
      
        fetchOrderDetails();
      }, [orderId, vippsOrderId, checkPhoneNumberInDatabase]);


    
        // Funksjon for å håndtere statusendring
        const handleStatusChange = async (newStatus) => {
            if (!order) return;
        
            // Bruk originalCustomerId her hvis nødvendig
            console.log("Kundens originale ID:", originalCustomerId);
        
            try {
                const response = await fetch(`${API_BASE_URL}/cluborders/${orderId}/update-status`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ status: newStatus, originalCustomerId }),
                });
        
                if (!response.ok) throw new Error("Kunne ikke oppdatere ordrestatus");
        
                setOrder((prevOrder) => ({ ...prevOrder, status: newStatus }));
                setStatusUpdated(true);
        
                setTimeout(() => {
                    setStatusUpdated(false);
                }, 5000);
            } catch (error) {
                console.error("Feil ved oppdatering av status:", error);
            }
        };
        

        const createNewCustomer = async (customerInfo) => {
            try {
                if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.phoneNumber) {
                    console.error("🚨 Feil: Kundeinformasjon mangler:", customerInfo);
                    throw new Error("Mangler nødvendig kundeinformasjon");
                }
        
                const newCustomer = {
                    firstName: customerInfo.firstName,
                    lastName: customerInfo.lastName,
                    phoneNumber: customerInfo.phoneNumber,
                    email: customerInfo.email || "",
                };
        
                console.log("📨 Sender forespørsel for å opprette kunde:", newCustomer);
        
                const response = await fetch(`${API_BASE_URL}/customers`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(newCustomer),
                });
        
                console.log("📩 Serverrespons status:", response.status);
        
                if (!response.ok) {
                    const errorMessage = await response.text();
                    console.error("❌ Serverfeil:", errorMessage);
                    throw new Error("Kunne ikke opprette kunde: " + errorMessage);
                }
        
                const createdCustomer = await response.json();
                console.log("✅ Kunde opprettet:", createdCustomer);
        
                // Oppdater state med den nye kunden
                setCustomerExists(true);
                setCustomerData(createdCustomer);
        
                return createdCustomer._id; 
        
            } catch (error) {
                console.error("🚨 Feil ved oppretting av ny kunde:", error);
                return null;
            }
        };
        
    
    
        const updateClubCustomerAndOrderWithOriginalId = async (clubCustomerId, customerId, orderId) => {
            try {
                await fetch(`${API_BASE_URL}/clubcustomers/${clubCustomerId}/update-original-id`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ originalCustomerId: customerId }),
                });
    
                await fetch(`${API_BASE_URL}/cluborders/${orderId}/update-original-id`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ originalCustomerId: customerId }),
                });
            } catch (error) {
                console.error("Feil ved oppdatering av ClubCustomer og ClubOrder:", error);
            }
        };
    
        const normalizePhoneNumber = (phoneNumber) => {
            return phoneNumber.replace(/\D/g, "").replace(/^47/, "");
        };
    

   

    useEffect(() => {
        if (order && order.items) {
            const initialQrCodes = {};
            order.items.forEach(item => {
                if (item.ean && [12, 13, 14].includes(item.ean.length)) {
                    initialQrCodes[item._id] = item.ean;
                }
                
            });
            setQrCodes(initialQrCodes);
        }
    }, [order]); // Denne kjører på nytt hver gang order oppdateres
    


const handleEanChange = async (index, value) => {
    console.log("EAN endres for produkt:", order.items[index].produktnavn);
    console.log("Ny EAN verdi:", value);

    setOrder(prevOrder => {
        const updatedItems = [...prevOrder.items];
        updatedItems[index] = { ...updatedItems[index], ean: value };
        return { ...prevOrder, items: updatedItems };
    });

    // Legg til logg for EAN-lengde
    console.log("EAN lengde:", value.length);

    // Lagre EAN i databasen hvis EAN har 12, 13 eller 14 sifre
    if (value.length >= 12 && value.length <= 14) {
        console.log("EAN er gyldig (12-14 sifre), lagrer EAN...");
        try {
            const response = await fetch(`${API_BASE_URL}/cluborders/${orderId}/update-ean`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ itemId: order.items[index]._id, ean: value }),
            });

            if (!response.ok) throw new Error("Kunne ikke lagre EAN i databasen");

            // Hent oppdatert ordre fra databasen
            const updatedOrderResponse = await fetch(`${API_BASE_URL}/cluborders/${orderId}`);
            if (!updatedOrderResponse.ok) throw new Error("Kunne ikke hente oppdatert ordredata");

            const updatedOrder = await updatedOrderResponse.json();
            setOrder(updatedOrder);

            // Sjekk at EAN faktisk er lagret i databasen
            const savedItem = updatedOrder.items.find(item => item._id === order.items[index]._id);
            if (savedItem && savedItem.ean === value) {
                console.log("EAN lagret i databasen:", savedItem.ean);
                setQrCodes(prev => {
                    // Forsikre oss om at vi ikke overskriver eksisterende QR-koder for andre produkter
                    const newQrCodes = { ...prev, [savedItem._id]: value };
                    return newQrCodes;
                });
            } else {
                console.log("Feil: EAN ble ikke lagret i databasen.");
            }

        } catch (err) {
            console.error("🚨 Feil ved lagring av EAN:", err);
            alert("Kunne ikke lagre EAN i databasen");
        }
    } else {
        // Fjern QR-koden dersom EAN ikke har 12, 13 eller 14 siffer
        console.log("EAN har ikke riktig lengde, fjerner QR-koden.");
        setQrCodes(prev => {
            const newQrCodes = { ...prev };
            delete newQrCodes[order.items[index]._id]; // Fjerner kun den aktuelle QR-koden
            return newQrCodes;
        });
    }
};
    
    
    const handleEanDelete = async (index) => {
        setOrder(prevOrder => {
            const updatedItems = [...prevOrder.items];
            updatedItems[index] = { ...updatedItems[index], ean: "" };
            return { ...prevOrder, items: updatedItems };
        });
    
        // Fjern QR-koden fra state
        setQrCodes(prev => {
            const newQrCodes = { ...prev };
            delete newQrCodes[order.items[index]._id];
            return newQrCodes;
        });
    
        try {
            const response = await fetch(`${API_BASE_URL}/cluborders/${orderId}/update-ean`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ itemId: order.items[index]._id, ean: "" }), // Tømmer EAN i databasen
            });
    
            if (!response.ok) throw new Error("Kunne ikke slette EAN i databasen");
    
        } catch (err) {
            console.error("🚨 Feil ved sletting av EAN:", err);
            alert("Kunne ikke slette EAN i databasen");
        }
    };
    
    

    if (loading) return <div className="text-center py-10">Laster ordre...</div>;
    if (error) return <div className="text-center py-10 text-red-500">Feil: {error}</div>;
    if (!order) return <div className="text-center py-10">Fant ikke ordren.</div>;

    // Generer et kort referansenummer basert på de siste 6 tegnene i orderId
    const hentNummer = order._id.slice(-6);

      // Beregn priser og plukkede produkter
    const produkterMedTrykk = order.items.map((item) => {
        const totalTrykkPris = item.trykk ? item.trykk.reduce((sum, t) => sum + t.pris, 0) : 0;
        return {
            ...item,
            totalTrykkPris,
            totalPris: item.klubbPris + totalTrykkPris, // Klubpris som base, trykk legges til
            erPlukket: Boolean(item.ean), // Sjekker om EAN er registrert
        };
    });

    const totalProdukterPris = produkterMedTrykk.reduce((sum, item) => sum + (item.klubbPris * item.quantity), 0);
    const totalTrykkPris = produkterMedTrykk.reduce((sum, item) => sum + (item.totalTrykkPris * item.quantity), 0);
    const totalOrdrePris = totalProdukterPris + totalTrykkPris;

    // Antall plukkede varer
    const antallPlukkede = produkterMedTrykk.filter(item => item.erPlukket).length;

    // Sum for plukkede varer (inkl. klubbpris + trykk)
    const sumPlukkedeProdukter = produkterMedTrykk
        .filter(item => item.erPlukket)
        .reduce((sum, item) => sum + (item.klubbPris * item.quantity), 0);

    const sumPlukkedeTrykk = produkterMedTrykk
        .filter(item => item.erPlukket)
        .reduce((sum, item) => sum + (item.totalTrykkPris * item.quantity), 0);

    const totalSumPlukkede = sumPlukkedeProdukter + sumPlukkedeTrykk;


    const handlePrint = async () => {
        if (!order) return;
    
        const plukkedeVarer = order.items.filter(item => item.ean && item.ean.length === 13);
    
        // Generer QR-koder som base64-data
        const qrDataUrls = {};
        await Promise.all(plukkedeVarer.map(async (item) => {
            qrDataUrls[item._id] = await QRCode.toDataURL(item.ean);
        }));
    
        // Kundeinformasjon
        const kundeInfo = `
            <div style="border: 1px solid #ddd; padding: 10px; margin-bottom: 15px; border-radius: 5px; background-color: #f9f9f9;">
                <h3 style="margin-bottom: 5px;">Kundeinformasjon</h3>
                <p style="margin: 3px 0;"><strong>Navn:</strong> ${escapeHtml(order.customerId?.firstName)} ${escapeHtml(order.customerId?.lastName)}</p>
                <p style="margin: 3px 0;"><strong>Telefon:</strong> ${escapeHtml(order.customerId?.phoneNumber)}</p>
                <p style="margin: 3px 0;"><strong>E-post:</strong> ${escapeHtml(order.customerId?.email)}</p>
                <p style="margin: 3px 0;"><strong>Adresse:</strong> ${escapeHtml(order.customerId?.address)}, ${escapeHtml(order.customerId?.postalCode)} ${escapeHtml(order.customerId?.city)}</p>
            </div>
        `;
    
        // Ordreinformasjon
        const ordreInfo = `
            <div style="border: 1px solid #ddd; padding: 10px; margin-bottom: 15px; border-radius: 5px; background-color: #f9f9f9;">
                <h3 style="margin-bottom: 5px;">Ordreinformasjon</h3>
                <p style="margin: 3px 0;"><strong>Hentenummer:</strong> ${order._id.slice(-6)}</p>
                <p style="margin: 3px 0;"><strong>Bestillingsdato:</strong> ${new Date(order.orderDate).toLocaleString("no-NO")}</p>
            </div>
        `;
    
        // Åpne utskriftsvindu
        const printWindow = window.open("", "_blank");
        printWindow.document.write(`
            <html>
            <head>
                <title>Klubb - Nettordre</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h2 { text-align: center; margin-bottom: 10px; }
                    h3 { margin-top: 15px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                    th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
                    th { background-color: #f4f4f4; font-size: 14px; }
                    td { font-size: 13px; }
                    .qr { text-align: center; }
                    .summary { margin-top: 15px; padding: 10px; background-color: #f9f9f9; border-radius: 5px; }
                    .summary p { margin: 5px 0; font-size: 14px; }
                </style>
            </head>
            <body>
                <h2>Klubb - Nettordre</h2>
    
                ${kundeInfo}  <!-- Kundeinformasjon legges til her -->
                ${ordreInfo}  <!-- Ordreinformasjon legges til her -->
    
                <h3>Produkter</h3>
                <table>
                    <thead>
                        <tr>
                            <th style="width: 25%;">Produkt</th>
                            <th style="width: 12%;">Størrelse</th>
                            <th style="width: 10%;">Antall</th>
                            <th style="width: 15%;">Klubbpris</th>
                            <th style="width: 15%;">Trykkpris</th>
                            <th style="width: 15%;">QR-kode</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${plukkedeVarer.map(item => `
                            <tr>
                                <td>${escapeHtml(item.produktnavn)}</td>
                                <td>${escapeHtml(item.størrelse)}</td>
                                <td>${escapeHtml(item.quantity)}</td>
                                <td>${escapeHtml(item.klubbPris)} NOK</td>
                                <td>${item.trykk ? item.trykk.reduce((sum, t) => sum + t.pris, 0) : 0} NOK</td>
                                <td class='qr'>
                                    <img src="${qrDataUrls[item._id] || ""}" width="50" height="50" />
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
    
                <div class="summary">
                    <h3>Oppsummering</h3>
                    <p><strong>Sum varer:</strong> ${plukkedeVarer.reduce((sum, item) => sum + (item.klubbPris * item.quantity), 0)} NOK</p>
                    <p><strong>Sum trykk:</strong> ${plukkedeVarer.reduce((sum, item) => sum + (item.trykk ? item.trykk.reduce((s, t) => s + t.pris, 0) : 0) * item.quantity, 0)} NOK</p>
                    <p><strong>Total:</strong> ${plukkedeVarer.reduce((sum, item) => sum + ((item.klubbPris + (item.trykk ? item.trykk.reduce((s, t) => s + t.pris, 0) : 0)) * item.quantity), 0)} NOK</p>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };



// Hent butikkId fra Cookies i stedet for sessionStorage
const butikkId = Cookies.get('butikkid') || null;

// Sammenlign butikkIdOrder med butikkId fra session
const erMatchendeButikkId = parseInt(butikkIdOrder, 10) === parseInt(butikkId, 10);

// Console log for å sjekke verdiene (kan fjernes etter testing)
console.log("butikkIdOrder:", butikkIdOrder);
console.log("butikkId fra session:", butikkId);
console.log("Er matchende butikkId?", erMatchendeButikkId);

// Funksjon som returnerer butikknavnet basert på butikkIdOrder
const getButikkNavn = (butikkIdOrder) => {
    switch (butikkIdOrder) {
        case "1":
            return "Sport 1 Røyken";
        case "2":
            return "Sport 1 Slemmestad";
        default:
            return "Ukjent butikk";
    }
};





    return (
        <div className="max-w-4xl mx-auto py-8 bg-white shadow-lg rounded-lg p-6">
            <h2 className="text-3xl font-semibold text-gray-800 mb-6">Ordre</h2>

{/* Link for å vise/lukke telefonnummer-sjekk */}
<button 
    onClick={togglePhoneCheck}
    className="text-blue-600 hover:underline mb-2"
>
    {showPhoneCheck ? "Lukk telefonnummer-sjekk" : "Åpne telefonnummer-sjekk"}
</button>

{/* Telefonnummer-sjekk (Vises bare hvis showPhoneCheck er true) */}
{showPhoneCheck && (
    <div className="border p-4 rounded-lg shadow-md bg-gray-100 mb-6">
        <h3 className="text-xl font-semibold mb-2">Telefonnummer-sjekk</h3>
        {customerExists === null ? (
            <p>Laster telefonnummer...</p>
        ) : customerExists ? (
            <p className="text-green-600">✅ Telefonnummer finnes i databasen.</p>
        ) : (
            <p className="text-red-600">❌ Telefonnummer er ikke lagret i databasen.</p>
        )}
    </div>
)}


            {/* Kundeinformasjon fra ordren */}
            <div className="border p-4 rounded-lg shadow-md bg-gray-100 mb-6">
                <h3 className="text-xl font-semibold mb-2">Kundeinformasjon fra ordre</h3>
                <p><strong>Navn:</strong> {order.customerId?.firstName} {order.customerId?.lastName}</p>
                <p><strong>Telefon:</strong> {order.customerId?.phoneNumber}</p>
                <p><strong>E-post:</strong> {order.customerId?.email}</p>
                <p><strong>Adresse:</strong> {order.customerId?.address}, {order.customerId?.postalCode} {order.customerId?.city}</p>
                {customerExists && (
            <button
    onClick={() => navigate('/sendsms', { 
        state: { 
            customer: customerData, 
            templateType: 'klubbnett', 
            orderDetails: order,
            status: order.status,  // Inkluderer status
            hentNummer: order._id.slice(-6)  // Sender hentenummeret som baseres på ID
        } 
    })}
    className="mt-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
>
    Send SMS
</button>


                )}
            </div>

{/* Link for å vise/lukke informasjon */}
<button 
    onClick={toggleCustomerInfo}
    className="text-blue-600 hover:underline mb-2"
>
    {showCustomerInfo ? "Lukk kundedatabase-informasjon" : "Åpne kundedatabase-informasjon"}
</button>

{/* Kundeinformasjon fra databasen (kun synlig når showCustomerInfo er true) */}
{showCustomerInfo && customerExists && customerData && (
    <div className="border p-4 rounded-lg shadow-md bg-gray-100 mb-6">
        <h3 className="text-xl font-semibold mb-2">Kundeinformasjon fra Kundedatabasen</h3>
        <p><strong>Kunde-ID (original):</strong> {customerData._id}</p>
        <p><strong>Navn:</strong> {customerData.firstName} {customerData.lastName}</p>
        <p><strong>Telefon:</strong> {customerData.phoneNumber}</p>
        <p><strong>E-post:</strong> {customerData.email}</p>
        <p><strong>Adresse:</strong> {customerData.address}, {customerData.postalCode} {customerData.city}</p>
        <p><strong>Registreringsdato:</strong> {new Date(customerData.registrationDate).toLocaleString("no-NO")}</p>

        {sisteStoreId && (
            <>
                <p><strong>Butikk (Store ID):</strong> {sisteStoreId}</p>
                <p><strong>Butikk Navn:</strong> {getButikkNavn(butikkIdOrder)}</p>
            </>
        )}
    </div>
)}




{/* Ordreinformasjon */}
<div className="border p-4 rounded-lg shadow-md bg-gray-100 mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-start">
    <div>
        <h3 className="text-xl font-semibold mb-2">Ordreinformasjon</h3>
        <p><strong>Fullt ordrenummer:</strong> {order._id}</p>
        <p><strong>Vipps Order ID:</strong> {order.vippsOrderId || "–"}</p>

                <p><strong>Hentekode:</strong> {hentNummer}</p>
                <p><strong>Dato:</strong> {new Date(order.orderDate).toLocaleString("no-NO")}</p>
                <p><strong>Antall produkter:</strong> {order.items.length}</p>
                {sisteStoreId && (
            <p><strong>Butikk (Store ID):</strong> {sisteStoreId}</p>
        )}
                {butikkIdOrder && (
                    <>
                        <p><strong>ButikkidOrder:</strong> {butikkIdOrder}</p>
                        <p><strong>Hentested:</strong> {getHentested(butikkIdOrder)}</p> {/* Vist butikkens navn basert på butikkIdOrder */}
                    </>
                )}

    </div>



    {/* Statusfeltet plassert til høyre */}
    <div className="text-right">
        <label className="block font-semibold mb-1"></label>
        <select
            className="border rounded p-2 bg-white min-w-[150px]"
            value={order.status}
            onChange={(e) => handleStatusChange(e.target.value)}
        >
            <option value="Aktiv">Aktiv</option>
            <option value="Sendt">Sendt</option>
            <option value="Avsluttet">Avsluttet</option>
            <option value="Kansellert">Kansellert</option>
        </select>

        {/* Statusoppdateringsmelding (vises under feltet) */}
        {statusUpdated && (
            <p className="text-green-600 font-semibold mt-2">
                ✅ Status er oppdatert.
            </p>
        )}
    </div>
</div>



            {/* Produkter */}
            <div className="border p-4 rounded-lg shadow-md bg-gray-100">
                <h3 className="text-xl font-semibold mb-4">Produkter i bestillingen</h3>
                <ul>
                    {produkterMedTrykk.map((item, index) => (
                        <li key={index} className="border-b py-4 flex flex-col lg:flex-row lg:items-center lg:space-x-4">
                            <div className="w-20 h-20 flex-shrink-0 border rounded overflow-hidden">
                                <img src={item.produktbilde || "/placeholder.jpg"} alt={item.produktnavn} className="object-cover w-full h-full" />
                            </div>
                            <div className="flex-1">
                                <p className="text-lg font-bold">{item.produktnavn}</p>
                                <p className="text-gray-600">Størrelse: {item.størrelse}</p>
                                <p className="text-gray-600">Antall: {item.quantity || 1}</p>
                                <p className="text-gray-600">Klubbpris: {item.klubbPris} NOK</p>
                                <p className="text-gray-600">Trykkpris: {item.totalTrykkPris} NOK</p>

                                {/* Trykk detaljer */}
                                {item.trykk && item.trykk.length > 0 && (
                                    <div className="mt-2">
                                        <h4 className="text-gray-800 font-semibold">Trykkdetaljer:</h4>
                                        <ul className="text-gray-600">
                                        {item.trykk.map((t, i) => (
    <li key={i}>
        {t.beskrivelse}: {t.pris} NOK{" "}
        {t.navn ? <strong>({t.navn})</strong> : t.initialer ? <strong>({t.initialer})</strong> : ""}
    </li>
))}


                                        </ul>
                                    </div>
                                )}

<div className="mt-2 relative w-52">
    <label className="block text-gray-600 text-sm">EAN-kode:</label>
    <div className="relative">
        <input
            type="number"
            value={item.ean || ""}
            onChange={(e) => handleEanChange(index, e.target.value)}
            className="border p-2 rounded w-full pr-8 appearance-none 
                      [&::-webkit-inner-spin-button]:appearance-none 
                      [&::-webkit-outer-spin-button]:appearance-none 
                      [-moz-appearance:textfield]" // Fjerner piler i alle nettlesere
        />
        {item.ean && (
            <button
                onClick={() => handleEanDelete(index)}
                className="absolute top-1/2 right-2 transform -translate-y-1/2 text-gray-500 hover:text-red-500"
            >
                ✖
            </button>
        )}
    </div>
    <p className={`text-sm mt-1 ${qrCodes[item._id] ? "text-green-600" : "text-red-600"}`}>
        {qrCodes[item._id]
            ? "✅ Varen er plukket og EAN er registrert."
            : "❌ Vare er ikke plukket."}
    </p>
</div>



                                {/* QR-kode visning */}
                                {qrCodes[item._id] && (
                                    <div className="mt-2">
                                        <QRCodeCanvas value={qrCodes[item._id]} size={60} />
                                    </div>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Oppsummering */}
            <div className="mt-6 border p-4 rounded-lg shadow-md bg-gray-100">
                <h3 className="text-xl font-semibold mb-4">Oppsummering</h3>
                <p><strong>Antall bestilte varer:</strong> {order.items.length}</p>
                <p><strong>Antall plukkede varer:</strong> {antallPlukkede}</p>
                <hr className="my-4 border-gray-300" />
                <p><strong>Sum varer (plukket):</strong> {sumPlukkedeProdukter} NOK</p>
                <p><strong>Sum trykk (plukket):</strong> {sumPlukkedeTrykk} NOK</p>
                <p><strong>Totalsum plukkede varer:</strong> {totalSumPlukkede} NOK</p>
                <hr className="my-4 border-gray-300" />
                <p><strong>Sum varer (bestilt):</strong> {totalProdukterPris} NOK</p>
                <p><strong>Sum trykk (bestilt):</strong> {totalTrykkPris} NOK</p>
                <p><strong>Totalsum bestilt:</strong> {totalOrdrePris} NOK</p>
            </div>


            <button onClick={() => navigate(-1)} className="mt-6 mr-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                Tilbake
            </button>
            <button onClick={handlePrint} className="mt-6 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800">
    Skriv ut regning
</button>



        </div>
    );
}

export default ClubOrderDetails;
